import { Link } from "react-router-dom";
import { Row } from "@/components/Row";
import { CardSkeletonRow, Spinner } from "@/components/Spinner";
import { TitleCard } from "@/components/TitleCard";
import { useResource } from "@/hooks/useResource";
import type { HomePayload, ProgressRow } from "@/api/types";

function formatRuntime(seconds: number): string {
  if (!seconds) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function Home() {
  const home = useResource<HomePayload>("/api/v1/discovery/home");
  const resume = useResource<{ results: ProgressRow[] }>("/api/v1/playback/continue-watching");

  if (home.loading) return <Spinner label="Loading your home page" />;
  if (home.error) {
    return (
      <main className="page container">
        <div className="alert alert--error" style={{ marginTop: 40 }}>
          {home.error.message}
        </div>
      </main>
    );
  }

  const hero = home.data?.hero;
  const progressByTitle = new Map(
    (resume.data?.results ?? []).map((row) => [row.title.id, row.percent_complete]),
  );

  return (
    <main className="page page--flush">
      {hero && (
        <section
          className="hero"
          style={{ background: hero.dominant_color }}
          aria-label={`Featured: ${hero.name}`}
        >
          {hero.backdrop_url && (
            <div className="hero__art" style={{ backgroundImage: `url(${hero.backdrop_url})` }} />
          )}
          <div className="hero__scrim" />
          <div className="hero__body">
            {hero.is_original && <span className="hero__eyebrow">Hayfish Original</span>}
            <h1 className="hero__title">{hero.name}</h1>
            <div className="hero__meta">
              <span className="chip">{hero.maturity_rating}</span>
              {hero.release_year && <span>{hero.release_year}</span>}
              {hero.kind === "movie" ? (
                <span>{formatRuntime(hero.runtime_seconds)}</span>
              ) : (
                <span>{hero.seasons?.length ?? 0} seasons</span>
              )}
              <span>{hero.genres.slice(0, 3).join(" · ")}</span>
            </div>
            <p className="hero__synopsis">{hero.short_synopsis || hero.synopsis}</p>
            <div className="hero__actions">
              <Link className="btn btn--primary" to={`/watch/${hero.id}`}>
                Play
              </Link>
              <Link className="btn btn--ghost" to={`/title/${hero.id}`}>
                More information
              </Link>
            </div>
          </div>
        </section>
      )}

      {(home.data?.rows ?? []).map((row) => (
        <Row key={row.key} title={row.title} reason={row.reason}>
          {row.items.map((item) => (
            <TitleCard
              key={`${row.key}-${item.id}`}
              title={item}
              wide={row.kind === "continue"}
              progress={row.kind === "continue" ? progressByTitle.get(item.id) : undefined}
            />
          ))}
        </Row>
      ))}

      {resume.loading && <CardSkeletonRow />}
    </main>
  );
}
